/**
 * ComfyUI'dan gelen 3D dosyayi (GLB/GLTF/STL/OBJ/PLY) FikoYT sahnesine ekler.
 *
 * NOT (renk): STL formati renk/doku tasimaz. Trellis2 is akisi dokulu **GLB**
 * uretir; renkli sonuc icin GLB kullanilir, STL yalnizca yedek yoldur.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { FikoCizimGLB } from '../FikoThreeCam/FikoCizimGLB';
import { FikoCizimSTL } from '../FikoThreeCam/FikoCizimSTL';
import { fikoOlcekleVeOturt as boyutlandir } from '../FikoThreeCam/FikoSTLDisaAktar';

/** GLB/GLTF -> FikoCizimGLB (renkli, dokulu). */
async function glbYukle(sahne: any, veri: ArrayBuffer, ad: string, hedefBoyutMm: number): Promise<any> {
  const gltf: any = await new Promise((cozum, hata) => {
    new GLTFLoader().parse(veri, '', cozum, hata);
  });

  const cizim: any = new FikoCizimGLB(sahne, ad);
  const cocuklar = [...(gltf.scene?.children ?? [])];
  if (!cocuklar.length) throw new Error('GLB icinde geometri yok.');
  cizim.add(...cocuklar);

  cizim.traverse((e: any) => {
    if (e.isMesh) {
      e.castShadow = true;
      e.receiveShadow = true;
      const m: any = e.material;
      if (m) {
        m.side = THREE.DoubleSide;
        m.polygonOffset = true;
        m.polygonOffsetFactor = 1;
        m.polygonOffsetUnits = 1;
        if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;   // renkler dogru gorunsun
        m.needsUpdate = true;
      }
      e.geometry?.computeVertexNormals?.();
    }
  });

  boyutlandir(cizim, hedefBoyutMm);
  return cizim;
}

/** STL -> FikoCizimSTL (tek renk; STL doku tasimaz). */
async function stlYukle(sahne: any, veri: ArrayBuffer, ad: string, hedefBoyutMm: number): Promise<any> {
  const geo: any = new STLLoader().parse(veri);
  geo.computeVertexNormals();
  const renkliMi = !!geo.getAttribute?.('color');
  const mat = new THREE.MeshStandardMaterial({
    color: renkliMi ? 0xffffff : 0xb8c4d0,
    vertexColors: renkliMi,
    metalness: 0.1, roughness: 0.75, side: THREE.DoubleSide
  });
  const cizim: any = new FikoCizimSTL(sahne, ad);
  cizim.add(new THREE.Mesh(geo, mat));
  boyutlandir(cizim, hedefBoyutMm);
  return cizim;
}

/**
 * Blob'u sahneye ekler. anaGosterim = FikoThreeJsComponent (fikoThreejs).
 * Donen deger: olusturulan FikoCizim nesnesi.
 */
export async function fikoAi3DSahneyeEkle(
  anaGosterim: any,
  blob: Blob,
  dosyaAdi: string,
  hedefBoyutMm = 100
): Promise<any> {
  const sahne = anaGosterim?.sahneler?.[0];
  if (!sahne) throw new Error('Aktif sahne bulunamadi.');

  const uzanti = (dosyaAdi.split('.').pop() || '').toLowerCase();
  const veri = await blob.arrayBuffer();

  let cizim: any;
  if (uzanti === 'glb' || uzanti === 'gltf') {
    cizim = await glbYukle(sahne, veri, dosyaAdi, hedefBoyutMm);
  } else if (uzanti === 'stl') {
    cizim = await stlYukle(sahne, veri, dosyaAdi, hedefBoyutMm);
  } else {
    throw new Error('Desteklenmeyen 3D format: .' + uzanti);
  }

  cizim.userData['fikoKaynak'] = 'ComfyUI / FikoYT-img2-3d';

  // Aktif sahnenin cizimler grubuna ekle (yoksa dogrudan sahneye)
  const hedef = sahne.cizimler ?? sahne;
  if (typeof hedef.ekle === 'function') hedef.ekle(cizim);
  else hedef.add(cizim);

  anaGosterim.ciz?.();
  anaGosterim.FikoYT?.degisti?.emit?.();
  try { sahne.ekranaSigdir?.(sahne); } catch { /* yoksay */ }

  return cizim;
}
